import { useMemo, useState } from "react";
import { fetchProductDetail } from "../../api/client.js";
import type { ProductCardDTO } from "../../api/types.js";
import { adaptCatalogProductV2, adaptCatalogProductsV2 } from "../../lib/catalog-shell-v2.js";
import { sortProducts } from "../../lib/sort-products.js";
import { messages, type SortOrder } from "../../i18n/messages.js";
import { useCatalog } from "../../hooks/useCatalog.js";
import { useCatalogCategories } from "../../hooks/useCatalogCategories.js";
import { ErrorState } from "../ErrorState.js";
import { LoadingState } from "../LoadingState.js";
import { AppButton } from "../ui/AppButton.js";
import { ProductCardSkeleton } from "../ui/Skeleton.js";
import { CatalogEmptyState } from "./CatalogEmptyState.js";
import { CategoryChips } from "./CategoryChips.js";
import { ProductCard } from "./ProductCard.js";
import { ProductDetailView } from "./ProductDetailView.js";
import { SearchInput } from "./SearchInput.js";

interface CatalogViewProps {
  initialCategory?: string | null;
  initialCollection?: string | null;
  onAddToCart: (productId: string, qty?: number) => Promise<void>;
  cartMutating?: boolean;
  catalogReadOnly?: boolean;
  onOpenAllCategories?: () => void;
  onCategoryChange?: (categoryId: string | null) => void;
  showStagingDebug?: boolean;
}

export function CatalogView({
  initialCategory = null,
  initialCollection = null,
  onAddToCart,
  cartMutating = false,
  catalogReadOnly = false,
  onOpenAllCategories,
  onCategoryChange,
  showStagingDebug = true,
}: CatalogViewProps) {
  const catalog = useCatalog({ category: initialCategory, collection: initialCollection });
  const categoriesState = useCatalogCategories();
  const [selectedProduct, setSelectedProduct] = useState<ProductCardDTO | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const adapted = useMemo(
    () => adaptCatalogProductsV2(catalog.results),
    [catalog.results],
  );
  const sortedResults = sortProducts(
    adapted.map((p) => p.raw),
    catalog.sortOrder,
  );
  const categoryData = categoriesState.data;

  function selectCategory(id: string | null) {
    catalog.setCategory(id);
    onCategoryChange?.(id);
  }

  function selectCollection(id: string | null) {
    catalog.setCollection(id);
  }

  async function openProduct(product: ProductCardDTO) {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const detail = await fetchProductDetail(product.product_id);
      setSelectedProduct(adaptCatalogProductV2(detail.product).raw);
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : messages.catalog.catalogFailed);
      setSelectedProduct(adaptCatalogProductV2(product).raw);
    } finally {
      setDetailLoading(false);
    }
  }

  if (selectedProduct && !detailLoading) {
    const related = sortedResults.filter(
      (item) =>
        item.product_id !== selectedProduct.product_id &&
        item.category === selectedProduct.category &&
        item.availability !== "out_of_stock" &&
        item.availability !== "unknown",
    );
    return (
      <ProductDetailView
        product={selectedProduct}
        relatedProducts={related}
        onBack={() => {
          setSelectedProduct(null);
          setDetailError(null);
        }}
        onOpenRelated={(item) => void openProduct(item)}
        {...(!catalogReadOnly
          ? {
              onAddToCart: async (productId: string, qty = 1) => {
                await onAddToCart(productId, qty);
              },
            }
          : {})}
        addDisabled={cartMutating || catalogReadOnly}
      />
    );
  }

  if (detailLoading) {
    return <LoadingState label={messages.catalog.openingProduct} />;
  }

  const totalCategories =
    (categoryData?.categories?.length ?? 0) ||
    (categoryData ? categoryData.primary.length + categoryData.overflow.length : 0);

  return (
    <section className="flex flex-1 flex-col pb-28">
      <SearchInput value={catalog.query} onChange={catalog.setQuery} />

      <div className="flex flex-wrap items-center gap-2 px-4 pb-2">
        <select
          value={catalog.sortOrder}
          onChange={(event) => catalog.setSortOrder(event.target.value as SortOrder)}
          aria-label={messages.catalog.sortLabel}
          className="min-h-11 flex-1 rounded-[14px] border border-black/10 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] px-3 text-sm"
        >
          <option value="default">{messages.catalog.sortDefault}</option>
          <option value="price_asc">{messages.catalog.sortPriceAsc}</option>
          <option value="price_desc">{messages.catalog.sortPriceDesc}</option>
          <option value="name">{messages.catalog.sortName}</option>
        </select>
      </div>

      <div className="px-4 pb-2">
        {categoriesState.loading ? (
          <p className="text-sm opacity-60">{messages.home.categoriesLoading}</p>
        ) : null}
        {categoryData ? (
          <CategoryChips
            collections={categoryData.collections}
            primary={categoryData.primary}
            overflow={categoryData.overflow}
            activeCategory={catalog.category}
            activeCollection={catalog.collection}
            onSelectCategory={selectCategory}
            onSelectCollection={selectCollection}
            {...(onOpenAllCategories ? { onOpenAllCategories } : {})}
          />
        ) : null}
      </div>

      {showStagingDebug && !catalog.loading ? (
        <p className="px-4 pb-1 text-[10px] opacity-50">
          staging · {import.meta.env.VITE_BUILD_ID || "no-build-id"} · loaded {sortedResults.length}
          {catalog.nextCursor ? "+" : ""} · cats {totalCategories}
          {catalog.query ? ` · q=${catalog.query}` : ""}
          {catalog.category ? ` · filter ${catalog.category}` : ""}
          {catalog.query && catalog.category ? " · mode=intersection" : ""}
        </p>
      ) : null}

      {catalog.loading ? (
        <div className="grid grid-cols-2 gap-3 p-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <ProductCardSkeleton key={index} />
          ))}
        </div>
      ) : null}

      {!catalog.loading && catalog.error ? (
        <ErrorState message={catalog.error} onRetry={catalog.retry} />
      ) : null}

      {!catalog.loading && !catalog.error && detailError ? (
        <p className="px-4 pt-2 text-xs text-amber-600">{detailError}</p>
      ) : null}

      {!catalog.loading && !catalog.error && sortedResults.length === 0 ? (
        <CatalogEmptyState
          query={catalog.query}
          category={catalog.category}
          onReset={() => {
            catalog.resetFilters();
            onCategoryChange?.(null);
          }}
        />
      ) : null}

      {!catalog.loading && !catalog.error && sortedResults.length > 0 ? (
        <ul className="grid grid-cols-2 gap-3 p-4" role="list">
          {sortedResults.map((product) => (
            <li key={product.id}>
              <ProductCard
                product={product}
                layout="grid"
                onSelect={(item) => void openProduct(item)}
                {...(!catalogReadOnly && product.purchasable
                  ? { onAddToCart: (item: ProductCardDTO) => void onAddToCart(item.product_id, 1) }
                  : {})}
                addDisabled={cartMutating || catalogReadOnly || !product.purchasable}
              />
            </li>
          ))}
        </ul>
      ) : null}

      {!catalog.loading && !catalog.error && catalog.nextCursor ? (
        <div className="px-4 pb-8">
          <AppButton
            variant="secondary"
            className="w-full"
            onClick={catalog.loadMore}
            disabled={catalog.loadingMore}
          >
            {catalog.loadingMore ? messages.catalog.loading : messages.common.loadMore}
          </AppButton>
        </div>
      ) : null}
    </section>
  );
}
